/** XeAgents.ai - Integrations Section with Business Tool Logos **/
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { HeartPulse } from "lucide-react";
import { useBranding } from "@/components/BrandingProvider";
import { usePilotRequestDialog } from "@/components/landing/PilotRequestDialog";

interface Integration {
  name: string;
  logoUrl: string;
  category: string;
  description: string;
}

const integrations: Integration[] = [
  // CRM
  {
    name: "Salesforce",
    logoUrl: "https://cdn.simpleicons.org/salesforce/00A1E0",
    category: "CRM",
    description: "Sync leads & contacts",
  },
  {
    name: "HubSpot",
    logoUrl: "https://cdn.simpleicons.org/hubspot/FF7A59",
    category: "CRM",
    description: "Marketing & sales pipeline",
  },
  {
    name: "Zoho CRM",
    logoUrl: "https://cdn.simpleicons.org/zoho/E42527",
    category: "CRM",
    description: "Customer management",
  },
  // eCommerce
  {
    name: "Shopify",
    logoUrl: "https://cdn.simpleicons.org/shopify/7AB55C",
    category: "eCommerce",
    description: "Order & customer support",
  },
  {
    name: "WooCommerce",
    logoUrl: "https://cdn.simpleicons.org/woocommerce/96588A",
    category: "eCommerce",
    description: "Store integration",
  },
  // Support
  {
    name: "Zendesk",
    logoUrl: "https://cdn.simpleicons.org/zendesk/03363D",
    category: "L1 Support",
    description: "Ticket management",
  },
  {
    name: "Freshdesk",
    logoUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2300A887'%3E%3Cpath d='M21.6 7.2H2.4C1.08 7.2 0 8.28 0 9.6v9.6c0 1.32 1.08 2.4 2.4 2.4h19.2c1.32 0 2.4-1.08 2.4-2.4V9.6c0-1.32-1.08-2.4-2.4-2.4zM6 18H3.6v-2.4H6V18zm0-4.8H3.6v-2.4H6v2.4zm4.8 4.8H8.4v-2.4h2.4V18zm0-4.8H8.4v-2.4h2.4v2.4zm4.8 4.8h-2.4v-2.4h2.4V18zm0-4.8h-2.4v-2.4h2.4v2.4zm4.8 4.8h-2.4v-2.4h2.4V18zm0-4.8h-2.4v-2.4h2.4v2.4zM21.6 4.8H2.4C1.08 4.8 0 3.72 0 2.4S1.08 0 2.4 0h19.2C22.92 0 24 1.08 24 2.4s-1.08 2.4-2.4 2.4z'/%3E%3C/svg%3E",
    category: "L1 Support",
    description: "Helpdesk automation",
  },
  {
    name: "Intercom",
    logoUrl: "https://cdn.simpleicons.org/intercom/6AFDEF",
    category: "Support",
    description: "Live chat & tickets",
  },
  // Invoicing & Payments
  {
    name: "QuickBooks",
    logoUrl: "https://cdn.simpleicons.org/quickbooks/2CA01C",
    category: "Invoicing",
    description: "Billing & invoices",
  },
  {
    name: "Xero",
    logoUrl: "https://cdn.simpleicons.org/xero/13B5EA",
    category: "Invoicing",
    description: "Accounting & finance",
  },
  // Scheduling
  {
    name: "Calendly",
    logoUrl: "https://cdn.simpleicons.org/calendly/006BFF",
    category: "Scheduling",
    description: "Appointment booking",
  },
  // Automation
  {
    name: "Zapier",
    logoUrl: "https://cdn.simpleicons.org/zapier/FF4A00",
    category: "Automation",
    description: "Connect 5,000+ apps",
  },
  // Communication
  {
    name: "Slack",
    logoUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23E01E5A' d='M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z'/%3E%3Cpath fill='%2336C5F0' d='M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z'/%3E%3Cpath fill='%232EB67D' d='M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.27 0a2.528 2.528 0 0 1-2.522 2.521 2.527 2.527 0 0 1-2.521-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.521 2.522v6.312z'/%3E%3Cpath fill='%23ECB22E' d='M15.165 18.956a2.528 2.528 0 0 1 2.521 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.521-2.522v-2.522h2.521zm0-1.27a2.527 2.527 0 0 1-2.521-2.522 2.527 2.527 0 0 1 2.521-2.521h6.313A2.528 2.528 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.521h-6.313z'/%3E%3C/svg%3E",
    category: "Communication",
    description: "Team notifications",
  },
  {
    name: "Microsoft Teams",
    logoUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%235059C9' d='M20.625 6.375h-3.75a1.875 1.875 0 0 0-1.875 1.875v6a3.375 3.375 0 0 0 6.75 0v-6.75a1.125 1.125 0 0 0-1.125-1.125zM19.5 4.5a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5z'/%3E%3Cpath fill='%237B83EB' d='M14.25 4.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm3 3H9.375a1.875 1.875 0 0 0-1.875 1.875v6.375a5.25 5.25 0 0 0 10.5 0V9.375A1.875 1.875 0 0 0 17.25 7.5z'/%3E%3Cpath fill='%234B53BC' d='M12 9H2.25A2.25 2.25 0 0 0 0 11.25v6A2.25 2.25 0 0 0 2.25 19.5H12a2.25 2.25 0 0 0 2.25-2.25v-6A2.25 2.25 0 0 0 12 9zm-1.5 7.5H4.875v-1.125h2.25v-3.75H5.813V10.5h4.687v1.125H9.375v3.75h1.125V16.5z'/%3E%3C/svg%3E",
    category: "Communication",
    description: "Enterprise messaging",
  },
  // Productivity
  {
    name: "Notion",
    logoUrl: "https://cdn.simpleicons.org/notion/000000",
    category: "Knowledge Base",
    description: "Docs & knowledge",
  },
  {
    name: "Google Sheets",
    logoUrl: "https://cdn.simpleicons.org/googlesheets/34A853",
    category: "Data",
    description: "Spreadsheet sync",
  },
];

/**
 * Medical software we can connect to on request. There is no built-in connector for any of
 * these yet, so they are shown as names (not logos) under an "on request" label; do not move
 * one into `integrations` above until a connector for it ships.
 */
const HEALTHCARE_SYSTEMS: { group: string; names: string[] }[] = [
  { group: "hospital", names: ["Epic", "Oracle Health (Cerner)", "athenahealth", "eClinicalWorks", "NextGen Healthcare", "MEDITECH"] },
  { group: "clinic", names: ["Practo", "Cliniko", "Halaxy", "Best Practice", "Medical Director", "HotDoc", "HealthEngine", "Tebra (Kareo)", "DrChrono", "OpenEMR"] },
];

function HealthcareSystems() {
  const { t } = useTranslation("landing");
  const { open: openPilotRequest } = usePilotRequestDialog();

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5 }}
      className="mb-8 rounded-2xl border border-teal-200/70 bg-white/80 p-5 shadow-sm backdrop-blur-sm sm:mb-10 sm:p-7"
      data-testid="integrations-healthcare"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-teal-50">
            <HeartPulse className="h-5 w-5 text-teal-600" />
          </span>
          <div>
            <h3 className="text-base font-semibold text-slate-800 sm:text-lg">{t("integrations.healthcare.title")}</h3>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-500">{t("integrations.healthcare.body")}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openPilotRequest}
          className="inline-flex flex-shrink-0 items-center justify-center rounded-full bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
          data-testid="button-request-integration"
        >
          {t("integrations.healthcare.cta")}
        </button>
      </div>

      <div className="mt-5 space-y-4">
        {HEALTHCARE_SYSTEMS.map(({ group, names }) => (
          <div key={group}>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
              {t(`integrations.healthcare.groups.${group}`)}
            </p>
            <ul className="mt-2 flex flex-wrap gap-2">
              {names.map((name) => (
                <li
                  key={name}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 sm:text-sm"
                >
                  {name}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <p className="mt-4 text-xs text-slate-400">{t("integrations.healthcare.note")}</p>
    </motion.div>
  );
}

// Category colors for badges
const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
  CRM: { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-200/50" },
  eCommerce: { bg: "bg-green-50", text: "text-green-600", border: "border-green-200/50" },
  "L1 Support": { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-200/50" },
  Support: { bg: "bg-purple-50", text: "text-purple-600", border: "border-purple-200/50" },
  Invoicing: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-200/50" },
  Scheduling: { bg: "bg-indigo-50", text: "text-indigo-600", border: "border-indigo-200/50" },
  Automation: { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-200/50" },
  Communication: { bg: "bg-pink-50", text: "text-pink-600", border: "border-pink-200/50" },
  "Knowledge Base": { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200/50" },
  Data: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200/50" },
};

const defaultCategoryColor = { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200/50" };

function IntegrationCard({ integration, index }: { integration: Integration; index: number }) {
  const { t } = useTranslation("landing");
  const colors = categoryColors[integration.category] || defaultCategoryColor;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className="group"
    >
      <motion.div
        className="relative bg-white/80 backdrop-blur-sm rounded-2xl border border-white/60 p-5 sm:p-6 flex flex-col items-center text-center gap-3 h-full shadow-sm hover:shadow-xl transition-all duration-300"
        whileHover={{ y: -6, scale: 1.02 }}
        style={{
          boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)",
        }}
      >
        {/* Logo Image */}
        <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-gray-50 to-white border border-gray-100/80 group-hover:border-gray-200 transition-colors duration-300 overflow-hidden p-2.5">
          <img
            src={integration.logoUrl}
            alt={`${integration.name} logo`}
            className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
            loading="lazy"
          />
        </div>

        {/* Name */}
        <h3 className="text-sm sm:text-base font-semibold text-slate-800 leading-tight">
          {integration.name}
        </h3>

        {/* Description */}
        <p className="text-xs text-slate-500 leading-relaxed">
          {t(`integrations.descriptions.${integration.name}`, { defaultValue: integration.description })}
        </p>

        {/* Category Badge */}
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
          {t(`integrations.categories.${integration.category}`, { defaultValue: integration.category })}
        </span>

        {/* Hover glow effect */}
        <div
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background: "radial-gradient(circle at 50% 0%, rgba(139,92,246,0.06) 0%, transparent 70%)",
          }}
        />
      </motion.div>
    </motion.div>
  );
}

function CentralHubBadge() {
  const { branding } = useBranding();
  const { t } = useTranslation("landing");

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.3 }}
      className="flex justify-center mb-8 sm:mb-10"
    >
      <div className="relative">
        {/* Animated rings */}
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)",
            width: "140%",
            height: "140%",
            left: "-20%",
            top: "-20%",
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="relative flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-full px-5 py-2.5 border border-white/60 shadow-lg">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <span className="text-sm font-semibold text-slate-700">
            {t("integrations.hub", { app: branding.app_name })}
          </span>
          <span className="text-sm font-bold text-violet-600">
            {t("integrations.hubTools", { count: integrations.length })}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function ConnectingLines() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <motion.div
        className="absolute w-72 h-72 rounded-full opacity-20"
        style={{
          background: "radial-gradient(circle, rgba(139,92,246,0.3), transparent 70%)",
          left: "8%",
          top: "18%",
        }}
        animate={{ x: [0, 20, 0], y: [0, -15, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-56 h-56 rounded-full opacity-15"
        style={{
          background: "radial-gradient(circle, rgba(59,130,246,0.3), transparent 70%)",
          right: "12%",
          bottom: "22%",
        }}
        animate={{ x: [0, -15, 0], y: [0, 20, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-44 h-44 rounded-full opacity-10"
        style={{
          background: "radial-gradient(circle, rgba(236,72,153,0.3), transparent 70%)",
          right: "38%",
          top: "55%",
        }}
        animate={{ x: [0, 10, 0], y: [0, -10, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

export function IntegrationsGrid() {
  const { branding } = useBranding();
  const { t } = useTranslation("landing");

  return (
    <section
      className="relative py-16 sm:py-20 md:py-28 lg:py-32 overflow-hidden"
      style={{
        background: "linear-gradient(160deg, #f0f4ff 0%, #faf5ff 30%, #fef3f2 60%, #fff7ed 100%)",
      }}
      data-testid="integrations-section"
    >
      <ConnectingLines />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-4 mb-10 sm:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-violet-50 border border-violet-200/60 rounded-full px-4 py-1.5 text-xs sm:text-sm font-medium text-violet-700 mb-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            {t("integrations.badge")}
          </motion.div>

          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold"
            data-testid="integrations-headline"
          >
            <span className="text-slate-800">{t("integrations.titleA")}</span>
            <span className="bg-gradient-to-r from-violet-600 via-indigo-500 to-blue-500 bg-clip-text text-transparent">
              {t("integrations.titleB")}
            </span>
          </h2>

          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            {t("integrations.subtitle", { app: branding.app_name })}
          </p>
        </motion.div>

        {/* Central Hub Badge */}
        <CentralHubBadge />

        <HealthcareSystems />

        {/* Integration Cards Grid */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5"
          data-testid="integrations-grid"
        >
          {integrations.map((integration, index) => (
            <IntegrationCard
              key={integration.name}
              integration={integration}
              index={index}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center mt-10 sm:mt-14"
        >
          <p className="text-sm text-slate-400">
            {t("integrations.more")}
          </p>
        </motion.div>
      </div>
    </section>
  );
}

export default IntegrationsGrid;
